import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { supabase } from "../lib/supabase";
import { removeTemplateMasters } from "../services/presentationUploadService";
import { useAuth } from "./EnterpriseAuthContext";
import {
  isMissingColumnError,
  SESSIONS_LIST_SELECT_WITH_EXTRACTION_CONFIG,
  SESSIONS_LIST_SELECT_LEGACY,
} from "../utils/supabaseSchemaFallback";
import { isMissingPageSetupColumn, pageSetupForNewDocument, warnMissingPageSetupColumn } from "../utils/pageSetup";
import {
  TEMPLATE_CONTENT_BATCH_SIZE,
  TEMPLATE_CONTENT_SELECT,
  TEMPLATE_CONTENT_SELECT_LEGACY,
  TEMPLATE_LIST_SELECT,
  TEMPLATE_LIST_SELECT_LEGACY,
  isTemplateContentLoaded,
  mergeTemplateRows,
  replaceTemplateList,
} from "../utils/templateListSelect";

/**
 * Run a write whose payload may carry `page_setup`; when the database does not have that column
 * yet (migration 20261105000100 not applied), run it once more without it so the rest still saves.
 * @param {object} payload - Row values.
 * @param {(values: object) => PromiseLike<{data: any, error: any}>} run - Builds and awaits the query.
 * @returns {Promise<{data: any, error: any}>} Query result.
 */
async function writeWithOptionalPageSetup(payload, run) {
  const result = await run(payload);
  if (!result.error || !payload || !("page_setup" in payload) || !isMissingPageSetupColumn(result.error)) return result;
  warnMissingPageSetupColumn();
  const { page_setup: _omit, ...rest } = payload;
  return run(rest);
}

/**
 * Run a templates read with the given select; when the database has no page_setup column yet,
 * run it once more with the legacy select (the same columns without page_setup).
 * @param {string} columns - Select with page_setup.
 * @param {string} legacyColumns - Same select without page_setup.
 * @param {(columns: string) => PromiseLike<{data: any, error: any}>} run - Builds and awaits the query.
 * @returns {Promise<{data: any, error: any}>} Query result.
 */
async function selectWithOptionalPageSetup(columns, legacyColumns, run) {
  const result = await run(columns);
  if (!result.error || !isMissingPageSetupColumn(result.error)) return result;
  warnMissingPageSetupColumn();
  return run(legacyColumns);
}

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

/**
 * Map a templates row to the local template shape.
 *
 * List rows (TEMPLATE_LIST_SELECT) have no `content`: they map to `contentLoaded: false` and
 * `content: undefined` until loadTemplate / loadTemplateContents fetches it. Full rows also carry
 * `sourceDocument` and `generationMetadata` when those columns were selected.
 * @param {object} data - Row.
 * @returns {object} Local template.
 */
function mapTemplateRow(data) {
  const contentLoaded = hasOwn(data, 'content');
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    content: contentLoaded ? data.content : undefined,
    contentLoaded,
    ...(hasOwn(data, 'source_document') ? { sourceDocument: data.source_document ?? null } : {}),
    ...(hasOwn(data, 'generation_metadata') ? { generationMetadata: data.generation_metadata ?? null } : {}),
    templateType: data.template_type || 'document', // Default to 'document' for backward compatibility
    status: data.status || 'ready',
    // null = default page setup; undefined while the page_setup column does not exist yet.
    pageSetup: data.page_setup ?? null,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
    userId: data.user_id,
  };
}

// Action types
const ActionTypes = {
  SET_LOADING: "SET_LOADING",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  SET_INITIAL_LOAD_COMPLETE: "SET_INITIAL_LOAD_COMPLETE",
  SET_CUSTOMERS: "SET_CUSTOMERS",
  ADD_CUSTOMER: "ADD_CUSTOMER",
  UPDATE_CUSTOMER: "UPDATE_CUSTOMER",
  DELETE_CUSTOMER: "DELETE_CUSTOMER",
  SET_PROJECTS: "SET_PROJECTS",
  ADD_PROJECT: "ADD_PROJECT",
  UPDATE_PROJECT: "UPDATE_PROJECT",
  DELETE_PROJECT: "DELETE_PROJECT",
  ADD_REQUIREMENT: "ADD_REQUIREMENT",
  ADD_REQUIREMENTS: "ADD_REQUIREMENTS",
  UPDATE_REQUIREMENT: "UPDATE_REQUIREMENT",
  DELETE_REQUIREMENT: "DELETE_REQUIREMENT",
  SET_TEMPLATES: "SET_TEMPLATES",
  MERGE_TEMPLATES: "MERGE_TEMPLATES",
  ADD_TEMPLATE: "ADD_TEMPLATE",
  UPDATE_TEMPLATE: "UPDATE_TEMPLATE",
  DELETE_TEMPLATE: "DELETE_TEMPLATE",
  SET_CURRENT_PROJECT: "SET_CURRENT_PROJECT",
  SET_CURRENT_TEMPLATE: "SET_CURRENT_TEMPLATE",
  RESET_TENANT_DATA: "RESET_TENANT_DATA",
  PATCH_GENERATED_DOCUMENT: "PATCH_GENERATED_DOCUMENT",
  UPSERT_GENERATED_DOCUMENT: "UPSERT_GENERATED_DOCUMENT",
  DELETE_GENERATED_DOCUMENT: "DELETE_GENERATED_DOCUMENT",
};

/**
 * Parses acceptance_criteria from DB (text/JSON) into an array.
 * @param {string|null|undefined} value - Raw value from DB.
 * @returns {string[]} Array of acceptance criteria strings.
 */
function parseAcceptanceCriteria(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return value.split("\n").filter((s) => s.trim());
  }
}

/**
 * Map a requirements row to the shape kept in project.requirements.
 * @param {object} row - requirements row.
 * @returns {object} Local requirement.
 */
function mapRequirementRow(row) {
  return {
    id: row.id,
    sessionId: row.session_id ?? null,
    title: row.title,
    description: row.description,
    type: row.type,
    priority: row.priority,
    acceptanceCriteria: parseAcceptanceCriteria(row.acceptance_criteria),
    dimensions: row.dimensions ?? null,
    createdAt: row.created_at,
  };
}

/** generated_documents columns and the local (project.generatedDocuments) keys they map to. */
const GENERATED_DOCUMENT_FIELDS = [
  ["name", "name"],
  ["content", "content"],
  ["template_id", "templateId"],
  ["template_name", "templateName"],
  ["requirements_count", "requirementsCount"],
  ["trust_metadata", "trustMetadata"],
  ["page_setup", "pageSetup"],
  ["created_at", "createdAt"],
  ["updated_at", "updatedAt"],
];

/**
 * Map a generated_documents row (snake_case, possibly with only some columns selected) or an
 * already-local document (camelCase) to the local shape. Only fields present in the input are
 * returned, so a partial row can be merged over an existing entry without clearing anything.
 * @param {object} doc - Row or local document.
 * @returns {object} Local document fields (always with `id`).
 */
function mapGeneratedDocumentRow(doc) {
  const mapped = { id: doc.id };
  GENERATED_DOCUMENT_FIELDS.forEach(([column, key]) => {
    if (doc[column] !== undefined) mapped[key] = doc[column];
    else if (doc[key] !== undefined) mapped[key] = doc[key];
  });
  return mapped;
}

/** Defaults for a document first added to local state from a partial row. */
const EMPTY_LOCAL_DOCUMENT = {
  name: "",
  content: "",
  templateId: null,
  templateName: null,
  requirementsCount: 0,
  trustMetadata: null,
  pageSetup: null,
  createdAt: null,
  updatedAt: null,
};

/**
 * Apply `fn` to one project's requirements (functional: always against the latest state).
 * @param {object} state - App state.
 * @param {string} projectId - Project id.
 * @param {(requirements: object[]) => object[]} fn - Returns the new list (same array = no change).
 * @returns {object} Next state.
 */
function updateProjectRequirements(state, projectId, fn) {
  let changed = false;
  const projects = state.projects.map((project) => {
    if (project.id !== projectId) return project;
    const current = project.requirements || [];
    const next = fn(current);
    if (next === current) return project;
    changed = true;
    return { ...project, requirements: next };
  });
  return changed ? { ...state, projects } : state;
}

// Initial state
const initialState = {
  customers: [],
  projects: [],
  templates: [],
  currentProject: null,
  currentTemplate: null,
  loading: true, // Start with loading to prevent flash of empty state
  initialLoadComplete: false, // Track if we've completed initial data load
  error: null,
};

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, loading: action.payload };

    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, loading: false };

    case ActionTypes.CLEAR_ERROR:
      return { ...state, error: null };

    case ActionTypes.SET_INITIAL_LOAD_COMPLETE:
      return { ...state, initialLoadComplete: action.payload };

    case ActionTypes.SET_CUSTOMERS:
      return { ...state, customers: action.payload, loading: false };

    case ActionTypes.ADD_CUSTOMER:
      return {
        ...state,
        customers: [...state.customers, action.payload],
        loading: false,
      };

    case ActionTypes.UPDATE_CUSTOMER:
      return {
        ...state,
        customers: state.customers.map((c) =>
          c.id === action.payload.id ? action.payload : c
        ),
        loading: false,
      };

    case ActionTypes.DELETE_CUSTOMER:
      return {
        ...state,
        customers: state.customers.filter((c) => c.id !== action.payload),
        loading: false,
      };

    case ActionTypes.SET_PROJECTS:
      return { ...state, projects: action.payload, loading: false };

    case ActionTypes.ADD_PROJECT:
      return {
        ...state,
        projects: [...state.projects, action.payload],
        loading: false,
      };

    case ActionTypes.UPDATE_PROJECT:
      return {
        ...state,
        projects: state.projects.map((p) =>
          p.id === action.payload.id ? action.payload : p
        ),
        currentProject:
          state.currentProject?.id === action.payload.id
            ? action.payload
            : state.currentProject,
        loading: false,
      };

    case ActionTypes.DELETE_PROJECT:
      return {
        ...state,
        projects: state.projects.filter((p) => p.id !== action.payload),
        currentProject:
          state.currentProject?.id === action.payload
            ? null
            : state.currentProject,
        loading: false,
      };

    case ActionTypes.ADD_REQUIREMENT:
      return {
        ...state,
        projects: state.projects.map((project) => {
          if (project.id === action.payload.projectId) {
            const requirements = project.requirements || [];
            return {
              ...project,
              requirements: [...requirements, action.payload.requirement],
            };
          }
          return project;
        }),
        loading: false,
      };

    case ActionTypes.ADD_REQUIREMENTS: {
      const { projectId, requirements: added } = action.payload;
      if (!added?.length) return state;
      return updateProjectRequirements(state, projectId, (requirements) => {
        const known = new Set(requirements.map((r) => r.id));
        const fresh = added.filter((r) => !known.has(r.id));
        return fresh.length ? [...requirements, ...fresh] : requirements;
      });
    }

    case ActionTypes.UPDATE_REQUIREMENT: {
      const { projectId, requirementId, patch } = action.payload;
      return updateProjectRequirements(state, projectId, (requirements) => {
        const index = requirements.findIndex((r) => r.id === requirementId);
        if (index === -1) return requirements;
        const next = requirements.slice();
        next[index] = { ...requirements[index], ...patch };
        return next;
      });
    }

    case ActionTypes.DELETE_REQUIREMENT: {
      const { projectId, requirementId } = action.payload;
      return updateProjectRequirements(state, projectId, (requirements) => {
        const next = requirements.filter((r) => r.id !== requirementId);
        return next.length === requirements.length ? requirements : next;
      });
    }

    // A reloaded list keeps the content already loaded for templates that did not change.
    case ActionTypes.SET_TEMPLATES:
      return { ...state, templates: replaceTemplateList(state.templates, action.payload), loading: false };

    // Full rows / content read for some templates (lazy loading); never touches loading.
    case ActionTypes.MERGE_TEMPLATES: {
      const { rows, addMissing = false } = action.payload || {};
      const templates = mergeTemplateRows(state.templates, rows, { addMissing });
      if (templates === state.templates) return state;
      const current = state.currentTemplate ? templates.find((t) => t.id === state.currentTemplate.id) : null;
      return { ...state, templates, currentTemplate: current || state.currentTemplate };
    }

    case ActionTypes.ADD_TEMPLATE:
      return {
        ...state,
        templates: [...state.templates, action.payload],
        loading: false,
      };

    case ActionTypes.UPDATE_TEMPLATE:
      return {
        ...state,
        templates: state.templates.map((t) =>
          t.id === action.payload.id ? action.payload : t
        ),
        currentTemplate:
          state.currentTemplate?.id === action.payload.id
            ? action.payload
            : state.currentTemplate,
        loading: false,
      };

    case ActionTypes.DELETE_TEMPLATE:
      return {
        ...state,
        templates: state.templates.filter((t) => t.id !== action.payload),
        currentTemplate:
          state.currentTemplate?.id === action.payload
            ? null
            : state.currentTemplate,
        loading: false,
      };

    case ActionTypes.SET_CURRENT_PROJECT:
      return { ...state, currentProject: action.payload };

    case ActionTypes.SET_CURRENT_TEMPLATE:
      return { ...state, currentTemplate: action.payload };

    // Account switch / user change: nothing of the previous tenant stays on screen while the
    // new account loads (and a loader that fails leaves an empty list, not stale data).
    case ActionTypes.RESET_TENANT_DATA:
      return {
        ...state,
        customers: [],
        projects: [],
        templates: [],
        currentProject: null,
        currentTemplate: null,
        error: null,
        loading: true,
      };

    case ActionTypes.PATCH_GENERATED_DOCUMENT: {
      const { documentId, patch } = action.payload;
      let changed = false;
      const projects = state.projects.map((project) => {
        const docs = project.generatedDocuments || [];
        const index = docs.findIndex((d) => d.id === documentId);
        if (index === -1) return project;
        const current = docs[index];
        if (Object.keys(patch).every((key) => current[key] === patch[key])) return project;
        changed = true;
        const generatedDocuments = docs.slice();
        generatedDocuments[index] = { ...current, ...patch };
        return { ...project, generatedDocuments };
      });
      return changed ? { ...state, projects } : state;
    }

    case ActionTypes.UPSERT_GENERATED_DOCUMENT: {
      const { projectId, document } = action.payload;
      if (!state.projects.some((p) => p.id === projectId)) return state;
      // The document may already be listed (possibly under another project, if it was moved).
      let existing = null;
      state.projects.forEach((project) => {
        const match = (project.generatedDocuments || []).find((d) => d.id === document.id);
        if (match) existing = match;
      });
      const merged = { ...EMPTY_LOCAL_DOCUMENT, ...existing, ...document };
      const projects = state.projects.map((project) => {
        const docs = project.generatedDocuments || [];
        const index = docs.findIndex((d) => d.id === document.id);
        if (project.id === projectId) {
          // Newest first, matching loadProjects (created_at descending).
          if (index === -1) return { ...project, generatedDocuments: [merged, ...docs] };
          const generatedDocuments = docs.slice();
          generatedDocuments[index] = merged;
          return { ...project, generatedDocuments };
        }
        if (index === -1) return project;
        return { ...project, generatedDocuments: docs.filter((d) => d.id !== document.id) };
      });
      return { ...state, projects };
    }

    case ActionTypes.DELETE_GENERATED_DOCUMENT: {
      const { projectId, documentId } = action.payload;
      let changed = false;
      const projects = state.projects.map((project) => {
        if (projectId && project.id !== projectId) return project;
        const docs = project.generatedDocuments || [];
        const next = docs.filter((d) => d.id !== documentId);
        if (next.length === docs.length) return project;
        changed = true;
        return { ...project, generatedDocuments: next };
      });
      return changed ? { ...state, projects } : state;
    }

    default:
      return state;
  }
}

// Create context
const AppContext = createContext();
/** Actions only (stable for the provider's lifetime): subscribe without re-rendering on state changes. */
const AppActionsContext = createContext(null);

/**
 * Build a plain-text representation from a structured Fathom utterance array.
 * @param {Array<object>} utterances - Array of utterance objects with speaker/text/timestamp.
 * @returns {string} Formatted plain-text transcript.
 */
const buildPlainTextFromUtterances = (utterances) => {
  if (!Array.isArray(utterances)) return "";
  return utterances
    .map((u) => {
      const name =
        u.speaker?.display_name || u.speaker || u.speaker_name || "Speaker";
      const ts = u.timestamp ? `[${u.timestamp}] ` : "";
      return `${ts}${name}: ${u.text || u.content || ""}`;
    })
    .join("\n");
};

/**
 * Parse a transcript value from the database, handling all known formats:
 *   - Structured transcript object with `content` key (new manual & Fathom sessions)
 *   - Legacy Fathom JSON `{ transcript: [...] }` stored as a stringified object
 *   - Legacy plain-text strings
 * @param {string|object|null} transcript - The raw transcript from the DB.
 * @returns {object|null} Normalised transcript object with at least a `content` key,
 *   or null if the transcript is empty.
 * @example
 *   parseTranscript('plain text')                     // { content: 'plain text' }
 *   parseTranscript({ content: '...' })               // returns as-is
 *   parseTranscript('{"content":"x"}')                // { content: 'x', ... }
 *   parseTranscript('{"transcript":[{speaker,text}]}') // builds content + keeps array
 */
const parseTranscript = (transcript) => {
  if (!transcript) return null;

  // Recall.ai format: transcript stored directly as a top-level array of utterances
  if (Array.isArray(transcript)) {
    return {
      content: buildPlainTextFromUtterances(transcript),
      contentType: "recall",
      source: "recall",
      transcript: transcript,
    };
  }

  // Already a structured object (JSONB column or pre-parsed)
  if (typeof transcript === "object" && transcript.content !== undefined) {
    return transcript;
  }

  // Object with a transcript utterance array but no content key (legacy Fathom JSONB)
  if (
    typeof transcript === "object" &&
    Array.isArray(transcript.transcript)
  ) {
    return {
      content: buildPlainTextFromUtterances(transcript.transcript),
      contentType: "fathom",
      source: "fathom",
      transcript: transcript.transcript,
    };
  }

  // Attempt to parse a JSON string (TEXT column storing stringified object)
  if (typeof transcript === "string") {
    try {
      const parsed = JSON.parse(transcript);
      // Recall.ai stringified array
      if (Array.isArray(parsed)) {
        return {
          content: buildPlainTextFromUtterances(parsed),
          contentType: "recall",
          source: "recall",
          transcript: parsed,
        };
      }
      if (parsed && typeof parsed === "object") {
        // New structured format with content key
        if (parsed.content !== undefined) {
          return parsed;
        }
        // Legacy Fathom format: { transcript: [{speaker, text, timestamp}, ...] }
        if (Array.isArray(parsed.transcript)) {
          return {
            content: buildPlainTextFromUtterances(parsed.transcript),
            contentType: "fathom",
            source: "fathom",
            transcript: parsed.transcript,
          };
        }
      }
    } catch {
      // Not JSON – treat as legacy plain-text transcript
    }

    // Legacy format: wrap plain text in a transcript object
    return { content: transcript };
  }

  return null;
};

/**
 * Extract the raw text content from a transcript value (structured or plain).
 * Useful when only the text body is needed (e.g. for AI extraction).
 * @param {string|object|null} transcript - The raw transcript from the DB.
 * @returns {string|null} The plain-text content, or null.
 */
const getTranscriptContent = (transcript) => {
  const parsed = parseTranscript(transcript);
  return parsed ? parsed.content : null;
};

// Provider component
/**
 * Scope key of the signed-in user in the active account.
 * @param {{id?: string}|null|undefined} user - Auth user.
 * @param {{id?: string}|null|undefined} account - Active account.
 * @returns {string|null} `<userId>:<accountId>`, or null when either is missing.
 */
function tenantScopeOf(user, account) {
  return user?.id && account?.id ? `${user.id}:${account.id}` : null;
}

export function AppProvider({ children }) {
  const { state: authState } = useAuth();
  const [state, dispatch] = useReducer(appReducer, initialState);
  /** Latest committed state, for actions that must check it after an await. */
  const stateRef = useRef(state);
  stateRef.current = state;
  /** Scope (`<userId>:<accountId>`) whose load is in flight, or null. */
  const isDataLoadingRef = useRef(null);
  const lastLoadedContextRef = useRef({
    userId: null,
    accountId: null,
  });
  /** Scope of the active user + account right now (null when signed out or without an account). */
  const tenantScope = tenantScopeOf(authState.user, authState.activeAccount);
  const tenantScopeRef = useRef(tenantScope);
  tenantScopeRef.current = tenantScope;
  /** Scope the customers/projects/templates in state belong to (null = nothing loaded). */
  const dataScopeRef = useRef(null);

  // Load projects from Supabase with related data
  const loadProjects = useCallback(async () => {
    const scope = tenantScopeOf(authState.user, authState.activeAccount);
    try {
      if (!authState.activeAccount) return;

      // Load projects with customer information.
      // First get all projects in the account, then filter for owned + shared projects
      const { data: allProjects, error: projectsError } = await supabase
        .from("projects")
        .select(
          `
            *,
            customers (
              id,
              name,
              description
            )
          `
        )
        .eq("account_id", authState.activeAccount.id)
        .order("created_at", { ascending: false });

      if (projectsError) throw projectsError;

      // Load project members to determine which projects are shared with this user
      const allProjectIds = allProjects?.map(p => p.id) || [];
      const { data: projectMembers, error: membersError } = await supabase
        .from("project_members")
        .select("project_id")
        .in("project_id", allProjectIds)
        .eq("user_id", authState.user.id);

      if (membersError) {
        console.warn("Could not load project members:", membersError);
      }

      const sharedProjectIds = new Set(projectMembers?.map(pm => pm.project_id) || []);

      // Filter to only show owned projects + shared projects
      const projects = allProjects?.filter(project =>
        project.user_id === authState.user.id || // Owned by user
        sharedProjectIds.has(project.id) // Shared with user
      ) || [];

      if (projectsError) throw projectsError;

      // Load sessions for all visible projects (exclude transcript). Retry if extraction_config column not migrated yet.
      const projectIds = projects.map(p => p.id);
      let sessionsQuery = supabase
        .from("sessions")
        .select(SESSIONS_LIST_SELECT_WITH_EXTRACTION_CONFIG)
        .in("project_id", projectIds)
        .order("created_at", { ascending: false });
      let { data: sessions, error: sessionsError } = await sessionsQuery;
      if (isMissingColumnError(sessionsError, "extraction_config")) {
        ({ data: sessions, error: sessionsError } = await supabase
          .from("sessions")
          .select(SESSIONS_LIST_SELECT_LEGACY)
          .in("project_id", projectIds)
          .order("created_at", { ascending: false }));
      }

      if (sessionsError) throw sessionsError;

      // Load requirements for all visible projects
      const { data: requirements, error: requirementsError } = await supabase
        .from("requirements")
        .select("*")
        .in("project_id", projectIds)
        .order("created_at", { ascending: false });

      if (requirementsError) throw requirementsError;

      // Load generated documents for all visible projects
      const { data: generatedDocuments, error: documentsError } = await supabase
        .from("generated_documents")
        .select("*")
        .in("project_id", projectIds)
        .order("created_at", { ascending: false });

      if (documentsError) throw documentsError;

      // Combine all data into projects
      const projectsWithData = (projects || []).map((project) => {
        const projectSessions = (sessions || [])
          .filter((s) => s.project_id === project.id)
          .map((s) => ({
            id: s.id,
            name: s.name,
            description: s.description,
            extractionType: s.extraction_type,
            extractionConfig: s.extraction_config || null,
            file: s.file_info,
            transcript: parseTranscript(s.transcript),
            summary: s.summary,
            source: s.source || "manual",
            sourceMetadata: s.source_metadata,
            createdAt: s.created_at,
          }));

        const projectRequirements = (requirements || [])
          .filter((r) => r.project_id === project.id)
          .map(mapRequirementRow);

        const projectDocuments = (generatedDocuments || [])
          .filter((d) => d.project_id === project.id)
          .map((d) => ({ ...EMPTY_LOCAL_DOCUMENT, ...mapGeneratedDocumentRow(d) }));

        return {
          id: project.id,
          name: project.name,
          description: project.description,
          customer_id: project.customer_id,
          customer: project.customers,
          createdAt: project.created_at,
          updatedAt: project.updated_at,
          userId: project.user_id,
          sessions: projectSessions,
          requirements: projectRequirements,
          generatedDocuments: projectDocuments,
        };
      });

      // The user switched account (or signed out) while this ran: drop the old account's rows.
      if (tenantScopeRef.current !== scope) return;
      dispatch({ type: ActionTypes.SET_PROJECTS, payload: projectsWithData });
    } catch (error) {
      console.error("Error loading projects:", error);
      if (tenantScopeRef.current !== scope) return;
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    }
  }, [authState.activeAccount, authState.user]);

  // Load templates from Supabase
  const loadTemplates = useCallback(async () => {
    const scope = tenantScopeOf(authState.user, authState.activeAccount);
    try {
      if (!authState.activeAccount) return;

      // The list never downloads content or the large jsonb columns: they are loaded when a
      // template is opened or used (loadTemplate / loadTemplateContents).
      const accountId = authState.activeAccount.id;
      const { data, error } = await selectWithOptionalPageSetup(TEMPLATE_LIST_SELECT, TEMPLATE_LIST_SELECT_LEGACY, (columns) =>
        supabase
          .from("templates")
          .select(columns)
          .eq("account_id", accountId)
          .order("created_at", { ascending: false })
      );

      if (error) throw error;

      // Map database fields to frontend conventions
      const templatesWithMappedFields = (data || []).map(mapTemplateRow);

      if (tenantScopeRef.current !== scope) return;
      dispatch({
        type: ActionTypes.SET_TEMPLATES,
        payload: templatesWithMappedFields,
      });
    } catch (error) {
      console.error("Error loading templates:", error);
      if (tenantScopeRef.current !== scope) return;
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    }
  }, [authState.activeAccount, authState.user]);

  /** In-flight single-template reads (`<scope>|<id>` -> promise), so one open does one read. */
  const templateLoadsRef = useRef(new Map());
  /** In-flight batch content reads, by `<scope>|<id>`. */
  const templateContentLoadsRef = useRef(new Map());

  /**
   * Load one template's full row (content, source_document, generation_metadata) into the list
   * and return it. Call before using `template.content` from the list: editors on open, generate,
   * preview, export, Inky. Already loaded templates resolve from state without a read unless
   * `force` is set (for example after an import job rewrote the row).
   *
   * Never touches the global loading/error state. A template that is not in the list yet (just
   * created elsewhere) is added to it.
   *
   * @param {string} templateId - Template id.
   * @param {{force?: boolean}} [options] - Options.
   * @returns {Promise<{success: boolean, data?: object, cached?: boolean, error?: string, code?: string}>}
   *   `code` is 'NOT_FOUND' (deleted, other workspace, no permission), 'NOT_AUTHENTICATED',
   *   'STALE' (the workspace changed during the read) or the PostgREST/Postgres code.
   */
  const loadTemplate = useCallback(async (templateId, options = {}) => {
    const { force = false } = options || {};
    if (!templateId) return { success: false, error: "No template id was given.", code: "NOT_FOUND" };
    const account = authState.activeAccount;
    if (!account) return { success: false, error: "User not authenticated", code: "NOT_AUTHENTICATED" };
    const scope = tenantScopeOf(authState.user, account);
    if (!force) {
      const cached = stateRef.current.templates.find((t) => t.id === templateId);
      if (cached && isTemplateContentLoaded(cached)) return { success: true, data: cached, cached: true };
    }
    const key = `${scope}|${templateId}`;
    const loads = templateLoadsRef.current;
    if (!force && loads.has(key)) return loads.get(key);

    const promise = (async () => {
      try {
        const { data, error } = await supabase
          .from("templates")
          .select("*")
          .eq("id", templateId)
          .eq("account_id", account.id)
          .maybeSingle();
        if (error) throw error;
        if (!data) {
          const missing = new Error("This template no longer exists here, or you no longer have permission to open it.");
          missing.code = "NOT_FOUND";
          throw missing;
        }
        if (tenantScopeRef.current !== scope) {
          return { success: false, error: "The workspace changed while the template was loading.", code: "STALE" };
        }
        const mapped = mapTemplateRow(data);
        // A save that finished while this read was in flight wins (it is newer).
        const existing = stateRef.current.templates.find((t) => t.id === templateId);
        const winner = existing ? mergeTemplateRows([existing], [mapped])[0] : mapped;
        dispatch({ type: ActionTypes.MERGE_TEMPLATES, payload: { rows: [mapped], addMissing: true } });
        return { success: true, data: winner };
      } catch (error) {
        console.error("Error loading template:", error);
        return { success: false, error: error.message, code: error.code };
      }
    })();
    loads.set(key, promise);
    promise.finally(() => {
      if (loads.get(key) === promise) loads.delete(key);
    });
    return promise;
  }, [authState.activeAccount, authState.user]);

  /**
   * Load the content of several templates into the list (one read per 50 ids), for views that
   * need it for many templates at once (quality scores, thumbnails). Templates already loaded or
   * loading are skipped. Rows come back with the list columns plus `content` (not the large jsonb
   * columns); use {@link loadTemplate} for one full row.
   *
   * @param {string[]} templateIds - Template ids.
   * @returns {Promise<{success: boolean, loaded: number, error?: string}>} Result.
   */
  const loadTemplateContents = useCallback(async (templateIds) => {
    const account = authState.activeAccount;
    if (!account) return { success: false, loaded: 0, error: "User not authenticated" };
    const scope = tenantScopeOf(authState.user, account);
    const wanted = [...new Set((Array.isArray(templateIds) ? templateIds : []).filter(Boolean))];
    const waits = new Set();
    const toFetch = [];
    wanted.forEach((id) => {
      const cached = stateRef.current.templates.find((t) => t.id === id);
      if (cached && isTemplateContentLoaded(cached)) return;
      const key = `${scope}|${id}`;
      const inflight = templateContentLoadsRef.current.get(key) || templateLoadsRef.current.get(key);
      if (inflight) waits.add(inflight);
      else toFetch.push(id);
    });

    for (let start = 0; start < toFetch.length; start += TEMPLATE_CONTENT_BATCH_SIZE) {
      const chunk = toFetch.slice(start, start + TEMPLATE_CONTENT_BATCH_SIZE);
      const promise = (async () => {
        try {
          const { data, error } = await selectWithOptionalPageSetup(TEMPLATE_CONTENT_SELECT, TEMPLATE_CONTENT_SELECT_LEGACY, (columns) =>
            supabase.from("templates").select(columns).eq("account_id", account.id).in("id", chunk)
          );
          if (error) throw error;
          if (tenantScopeRef.current !== scope) return { success: false, loaded: 0, error: "The workspace changed while templates were loading." };
          const rows = (data || []).map(mapTemplateRow);
          if (rows.length) dispatch({ type: ActionTypes.MERGE_TEMPLATES, payload: { rows } });
          return { success: true, loaded: rows.length };
        } catch (error) {
          console.error("Error loading template content:", error);
          return { success: false, loaded: 0, error: error.message };
        }
      })();
      const loads = templateContentLoadsRef.current;
      chunk.forEach((id) => loads.set(`${scope}|${id}`, promise));
      promise.finally(() => {
        chunk.forEach((id) => {
          const key = `${scope}|${id}`;
          if (loads.get(key) === promise) loads.delete(key);
        });
      });
      waits.add(promise);
    }

    const results = await Promise.all([...waits]);
    const failed = results.find((r) => !r?.success);
    return {
      success: !failed,
      loaded: results.reduce((sum, r) => sum + (r?.loaded ?? (r?.data ? 1 : 0)), 0),
      ...(failed ? { error: failed.error } : {}),
    };
  }, [authState.activeAccount, authState.user]);

  // Load customers from Supabase
  const loadCustomers = useCallback(async () => {
    const scope = tenantScopeOf(authState.user, authState.activeAccount);
    try {
      if (!authState.activeAccount) return;

      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("account_id", authState.activeAccount.id)
        .order("name", { ascending: true });

      if (error) throw error;

      // Map database fields to frontend conventions
      const customersWithMappedFields = (data || []).map((customer) => ({
        id: customer.id,
        name: customer.name,
        description: customer.description,
        contact_email: customer.contact_email,
        contact_phone: customer.contact_phone,
        company_website: customer.company_website,
        address: customer.address,
        domain: customer.domain || [],
        createdAt: customer.created_at,
        updatedAt: customer.updated_at,
        userId: customer.user_id,
      }));

      if (tenantScopeRef.current !== scope) return;
      dispatch({
        type: ActionTypes.SET_CUSTOMERS,
        payload: customersWithMappedFields,
      });
    } catch (error) {
      console.error("Error loading customers:", error);
      if (tenantScopeRef.current !== scope) return;
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
    }
  }, [authState.activeAccount, authState.user]);

  // Load data when the user or the active account changes
  // Inky (server or browser) saved customers, projects or meeting links: reload the lists so new
  // records appear and the links on its Done cards open real pages.
  useEffect(() => {
    const onChanged = (event) => {
      const kind = event?.detail?.kind;
      if (kind === "customer") loadCustomers();
      else if (kind === "project" || kind === "session") loadProjects();
    };
    window.addEventListener("quillai:data-changed", onChanged);
    return () => window.removeEventListener("quillai:data-changed", onChanged);
  }, [loadCustomers, loadProjects]);

  useEffect(() => {
    // Account switch, user change or losing the account: clear the previous tenant's data
    // right away (not after the debounce), so it never shows under the new account.
    if (dataScopeRef.current !== null && dataScopeRef.current !== tenantScope) {
      dataScopeRef.current = null;
      dispatch({ type: ActionTypes.RESET_TENANT_DATA });
      if (!tenantScope) dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }

    const loadAllData = async () => {
      const currentUserId = authState.user?.id || null;
      const currentAccountId = authState.activeAccount?.id || null;
      const authReady =
        authState.isAuthenticated &&
        !!currentUserId &&
        !!currentAccountId &&
        !authState.loading;

      // Clear cached state immediately when the user logs out
      if (!authState.isAuthenticated || !authState.user) {
        dispatch({ type: ActionTypes.SET_CUSTOMERS, payload: [] });
        dispatch({ type: ActionTypes.SET_PROJECTS, payload: [] });
        dispatch({ type: ActionTypes.SET_TEMPLATES, payload: [] });
        dispatch({ type: ActionTypes.SET_CURRENT_PROJECT, payload: null });
        dispatch({ type: ActionTypes.SET_CURRENT_TEMPLATE, payload: null });
        dispatch({ type: ActionTypes.SET_LOADING, payload: false });
        dispatch({ type: ActionTypes.SET_INITIAL_LOAD_COMPLETE, payload: false });
        isDataLoadingRef.current = null;
        dataScopeRef.current = null;
        lastLoadedContextRef.current = { userId: null, accountId: null };
        return;
      }

      if (!authReady) {
        return;
      }

      const contextChanged =
        lastLoadedContextRef.current.userId !== currentUserId ||
        lastLoadedContextRef.current.accountId !== currentAccountId ||
        // Cleared by an account switch (e.g. A -> B -> A within the debounce): reload.
        dataScopeRef.current !== tenantScopeOf(authState.user, authState.activeAccount);

      // Prevent redundant reloads triggered by visibility changes for the same context
      if (
        !contextChanged &&
        document.visibilityState === "visible" &&
        state.initialLoadComplete
      ) {
        return;
      }

      // Prevent multiple simultaneous loads of the same scope (a load for another account
      // may still be in flight: its results are discarded by the loaders' scope check).
      const loadScope = tenantScopeOf(authState.user, authState.activeAccount);
      if (isDataLoadingRef.current === loadScope) return;

      isDataLoadingRef.current = loadScope;
      dataScopeRef.current = loadScope;
      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      try {
        // Load all data concurrently
        await Promise.allSettled([
          loadCustomers(),
          loadProjects(),
          loadTemplates(),
        ]);
        if (tenantScopeRef.current === loadScope) {
          lastLoadedContextRef.current = {
            userId: currentUserId,
            accountId: currentAccountId,
          };
        }
      } finally {
        if (isDataLoadingRef.current === loadScope) {
          isDataLoadingRef.current = null;
          dispatch({ type: ActionTypes.SET_LOADING, payload: false });
          dispatch({ type: ActionTypes.SET_INITIAL_LOAD_COMPLETE, payload: true });
        }
      }
    };

    // Debounce the data loading to prevent rapid successive calls
    const debounceTimeout = setTimeout(() => {
      loadAllData();
    }, 200); // Increased debounce to 200ms for more stability

    return () => clearTimeout(debounceTimeout);
  }, [
    tenantScope,
    authState.isAuthenticated,
    authState.user,
    authState.activeAccount,
    authState.loading, // Add auth loading state as dependency
    state.initialLoadComplete, // Add to prevent unnecessary reloads on visibility changes
    loadCustomers,
    loadProjects,
    loadTemplates,
  ]);

  // Project actions
  const createProject = async (projectData) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");
      if (!authState.activeAccount.id) throw new Error("No active account ID available");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const { data, error } = await supabase
        .from("projects")
        .insert([
          {
            ...projectData,
            user_id: authState.user.id,
            account_id: authState.activeAccount.id, // Add account_id
          },
        ])
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Map database fields to frontend conventions
      const mappedProject = {
        id: data.id,
        name: data.name,
        description: data.description,
        customer_id: data.customer_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        userId: data.user_id,
        sessions: [],
        requirements: [],
        generatedDocuments: [],
      };

      dispatch({ type: ActionTypes.ADD_PROJECT, payload: mappedProject });
      return { success: true, data: mappedProject };
    } catch (error) {
      console.error("Error creating project:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  const updateProject = async (projectId, updates) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const { data, error } = await supabase
        .from("projects")
        .update(updates)
        .eq("id", projectId)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // If customer_id was updated, reload projects to get updated customer info
      if (updates.customer_id) {
        await loadProjects();
        return { success: true, data };
      }

      // Map database fields to frontend conventions
      const mappedProject = {
        id: data.id,
        name: data.name,
        description: data.description,
        customer_id: data.customer_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        userId: data.user_id,
      };

      dispatch({ type: ActionTypes.UPDATE_PROJECT, payload: mappedProject });
      return { success: true, data: mappedProject };
    } catch (error) {
      console.error("Error updating project:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  const deleteProject = async (projectId) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", projectId)
        .eq("account_id", authState.activeAccount.id);

      if (error) {
        throw error;
      }

      dispatch({ type: ActionTypes.DELETE_PROJECT, payload: projectId });
      return { success: true };
    } catch (error) {
      console.error("Error deleting project:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  // Template actions
  const createTemplate = async (templateData) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const { data, error } = await writeWithOptionalPageSetup(
        {
          ...templateData,
          user_id: authState.user.id,
          account_id: authState.activeAccount.id, // Add account_id
        },
        (row) => supabase.from("templates").insert([row]).select().single()
      );

      if (error) {
        throw error;
      }

      // Map database fields to frontend conventions
      const mappedTemplate = mapTemplateRow(data);

      dispatch({ type: ActionTypes.ADD_TEMPLATE, payload: mappedTemplate });
      return { success: true, data: mappedTemplate };
    } catch (error) {
      console.error("Error creating template:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  /**
   * Update a template in the active workspace.
   *
   * Options for editors that autosave:
   * - `expectedUpdatedAt`: only write when the row's `updated_at` still matches (optimistic
   *   concurrency). A changed row returns `{ success: false, conflict: true, data }` with the
   *   server's current version instead of overwriting it.
   * - `force`: overwrite regardless (after the user chose "Keep mine").
   * - `silent`: do not touch the global loading/error state (background saves).
   * Failures return `{ success: false, error, code }`; `code` is the PostgREST/Postgres code, or
   * 'NOT_FOUND' (no row: deleted, other workspace, or no permission) / 'NOT_AUTHENTICATED'.
   *
   * @param {string} templateId - Template id.
   * @param {object} updates - Column updates.
   * @param {{expectedUpdatedAt?: string|null, force?: boolean, silent?: boolean}} [options] - Options.
   * @returns {Promise<{success: boolean, data?: object, error?: string, code?: string, status?: number, conflict?: boolean}>} Result.
   */
  const updateTemplate = async (templateId, updates, options = {}) => {
    const { expectedUpdatedAt = null, force = false, silent = false } = options || {};
    try {
      if (!authState.activeAccount) {
        const notSignedIn = new Error("User not authenticated");
        notSignedIn.code = "NOT_AUTHENTICATED";
        throw notSignedIn;
      }
      const accountId = authState.activeAccount.id;

      if (!silent) dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const conditional = !force && Boolean(expectedUpdatedAt);
      // Versioned writes bump updated_at themselves so other open editors can detect the change.
      const payload = conditional || force ? { ...updates, updated_at: new Date().toISOString() } : updates;
      // page_setup (when given) is part of the same conditional UPDATE as the content.
      const { data, error } = await writeWithOptionalPageSetup(payload, (values) => {
        let query = supabase
          .from("templates")
          .update(values)
          .eq("id", templateId)
          .eq("account_id", accountId);
        if (conditional) query = query.eq("updated_at", expectedUpdatedAt);
        return query.select().maybeSingle();
      });

      if (error) {
        throw error;
      }

      if (!data) {
        if (conditional) {
          const { data: current, error: readError } = await supabase
            .from("templates")
            .select("*")
            .eq("id", templateId)
            .eq("account_id", accountId)
            .maybeSingle();
          if (readError) throw readError;
          if (current && current.updated_at !== expectedUpdatedAt) {
            const serverTemplate = mapTemplateRow(current);
            dispatch({ type: ActionTypes.UPDATE_TEMPLATE, payload: serverTemplate });
            return {
              success: false,
              conflict: true,
              data: serverTemplate,
              error: "This template was changed elsewhere since you opened it.",
            };
          }
        }
        const missing = new Error("This template no longer exists here, or you no longer have permission to edit it.");
        missing.code = "NOT_FOUND";
        throw missing;
      }

      const mappedTemplate = mapTemplateRow(data);
      dispatch({ type: ActionTypes.UPDATE_TEMPLATE, payload: mappedTemplate });
      return { success: true, data: mappedTemplate };
    } catch (error) {
      console.error("Error updating template:", error);
      if (!silent) dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message, code: error.code, status: error.status };
    }
  };

  const deleteTemplate = async (templateId) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const accountId = authState.activeAccount.id;
      const known = stateRef.current.templates.find((t) => t.id === templateId);
      const { error } = await supabase
        .from("templates")
        .delete()
        .eq("id", templateId)
        .eq("account_id", accountId);

      if (error) {
        throw error;
      }

      // The original PowerPoint (and its earlier revisions) must not outlive the template.
      if (!known || known.templateType === "powerpoint") {
        await removeTemplateMasters(supabase, accountId, templateId);
      }

      dispatch({ type: ActionTypes.DELETE_TEMPLATE, payload: templateId });
      return { success: true };
    } catch (error) {
      console.error("Error deleting template:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  // Current item setters
  const setCurrentProject = (project) => {
    dispatch({ type: ActionTypes.SET_CURRENT_PROJECT, payload: project });
  };

  const setCurrentTemplate = (template) => {
    dispatch({ type: ActionTypes.SET_CURRENT_TEMPLATE, payload: template });
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  };

  // Utility functions
  const getProjectById = (projectId) => {
    return state.projects.find((p) => p.id === projectId);
  };

  const getTemplateById = (templateId) => {
    return state.templates.find((t) => t.id === templateId);
  };

  // Session actions
  const addSession = async (sessionData) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const insertPayload = {
        user_id: authState.user.id,
        name: sessionData.name,
        description: sessionData.description || "",
        extraction_type: sessionData.extractionType || null,
        extraction_config: sessionData.extractionConfig || null,
        file_info: sessionData.file || null,
        created_at: new Date().toISOString(),
      };

      // Only include project_id if provided (nullable for unlinked sessions)
      if (sessionData.projectId) {
        insertPayload.project_id = sessionData.projectId;
      }

      // Include optional fields – stringify transcript objects for TEXT columns
      if (sessionData.transcript) {
        insertPayload.transcript =
          typeof sessionData.transcript === "object"
            ? JSON.stringify(sessionData.transcript)
            : sessionData.transcript;
      }
      if (sessionData.summary) insertPayload.summary = sessionData.summary;
      if (sessionData.source) insertPayload.source = sessionData.source;
      if (sessionData.sourceMetadata) insertPayload.source_metadata = sessionData.sourceMetadata;
      if (sessionData.accountId) insertPayload.account_id = sessionData.accountId;

      let { data, error } = await supabase
        .from("sessions")
        .insert([insertPayload])
        .select()
        .single();

      if (isMissingColumnError(error, "extraction_config") && insertPayload.extraction_config !== undefined) {
        const { extraction_config: _omit, ...rest } = insertPayload;
        ({ data, error } = await supabase
          .from("sessions")
          .insert([rest])
          .select()
          .single());
      }

      if (error) {
        throw error;
      }

      // Update the project with the new session (only if linked to a project)
      if (sessionData.projectId) {
        const updatedProjects = state.projects.map((project) => {
          if (project.id === sessionData.projectId) {
            const sessions = project.sessions || [];
            return {
              ...project,
              sessions: [
                ...sessions,
                {
                  id: data.id,
                  name: data.name,
                  description: data.description,
                  extractionType: data.extraction_type,
                  extractionConfig: data.extraction_config ?? sessionData.extractionConfig ?? null,
                  file: data.file_info,
                  transcript: parseTranscript(data.transcript),
                  summary: data.summary,
                  source: data.source || "manual",
                  sourceMetadata: data.source_metadata,
                  createdAt: data.created_at,
                },
              ],
            };
          }
          return project;
        });

        dispatch({ type: ActionTypes.SET_PROJECTS, payload: updatedProjects });
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error adding session:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  };

  const deleteSession = async (projectId, sessionId) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      // Delete session and associated requirements
      const { error: sessionError } = await supabase
        .from("sessions")
        .delete()
        .eq("id", sessionId)
        .eq("user_id", authState.user.id);

      if (sessionError) {
        throw sessionError;
      }

      // Also delete associated requirements
      const { error: requirementsError } = await supabase
        .from("requirements")
        .delete()
        .eq("session_id", sessionId)
        .eq("user_id", authState.user.id);

      if (requirementsError) {
        console.warn(
          "Error deleting associated requirements:",
          requirementsError
        );
      }

      // Update the project by removing the session and its requirements
      const updatedProjects = state.projects.map((project) => {
        if (project.id === projectId) {
          const sessions = (project.sessions || []).filter(
            (s) => s.id !== sessionId
          );
          const requirements = (project.requirements || []).filter(
            (r) => r.sessionId !== sessionId
          );
          return {
            ...project,
            sessions,
            requirements,
          };
        }
        return project;
      });

      dispatch({ type: ActionTypes.SET_PROJECTS, payload: updatedProjects });
      return { success: true };
    } catch (error) {
      console.error("Error deleting session:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: ActionTypes.SET_LOADING, payload: false });
    }
  };

  /**
   * Link an unlinked session to a project.
   * @param {string} sessionId - The session ID to link.
   * @param {string} projectId - The project ID to link to.
   * @returns {Promise<{success: boolean, error?: string}>} Result.
   */
  const linkSession = async (sessionId, projectId) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("sessions")
        .update({ project_id: projectId })
        .eq("id", sessionId)
        .eq("user_id", authState.user.id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error("Error linking session:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Update a session's editable fields (name, description, transcript).
   * Persists to the database and refreshes local project state.
   * @param {string} projectId - The project the session belongs to.
   * @param {string} sessionId - The session ID to update.
   * @param {object} updates - Fields to update: { name?, description?, transcript? }.
   * @returns {Promise<{success: boolean, error?: string}>} Result.
   */
  const updateSession = async (projectId, sessionId, updates) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const payload = {};
      if (updates.name !== undefined) payload.name = updates.name;
      if (updates.description !== undefined) payload.description = updates.description;
      if (updates.extractionType !== undefined) payload.extraction_type = updates.extractionType;
      if (updates.extractionConfig !== undefined) payload.extraction_config = updates.extractionConfig;
      if (updates.transcript !== undefined) {
        payload.transcript =
          typeof updates.transcript === "object"
            ? JSON.stringify(updates.transcript)
            : updates.transcript;
      }

      let { data, error } = await supabase
        .from("sessions")
        .update(payload)
        .eq("id", sessionId)
        .eq("user_id", authState.user.id)
        .select()
        .single();

      if (isMissingColumnError(error, "extraction_config") && payload.extraction_config !== undefined) {
        const { extraction_config: _omit, ...restPayload } = payload;
        ({ data, error } = await supabase
          .from("sessions")
          .update(restPayload)
          .eq("id", sessionId)
          .eq("user_id", authState.user.id)
          .select()
          .single());
      }

      if (error) throw error;

      const mergedExtractionConfig =
        data.extraction_config !== undefined && data.extraction_config !== null
          ? data.extraction_config
          : updates.extractionConfig;

      // Refresh local state
      if (projectId) {
        const updatedProjects = state.projects.map((project) => {
          if (project.id === projectId) {
            return {
              ...project,
              sessions: (project.sessions || []).map((s) =>
                s.id === sessionId
                  ? {
                    ...s,
                    name: data.name,
                    description: data.description,
                    transcript: parseTranscript(data.transcript),
                    ...(data.extraction_type !== undefined && { extractionType: data.extraction_type }),
                    ...(mergedExtractionConfig !== undefined && { extractionConfig: mergedExtractionConfig }),
                  }
                  : s
              ),
            };
          }
          return project;
        });
        dispatch({ type: ActionTypes.SET_PROJECTS, payload: updatedProjects });
      }

      return { success: true, data };
    } catch (error) {
      console.error("Error updating session:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Update the transcript content of a session (used during regeneration).
   * @param {string} sessionId - The session ID to update.
   * @param {string|object} transcript - The new transcript content (plain string or structured object).
   * @returns {Promise<{success: boolean, error?: string}>} Result.
   */
  const updateSessionTranscript = async (sessionId, transcript) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const serialised =
        typeof transcript === "object"
          ? JSON.stringify(transcript)
          : transcript;

      const { error } = await supabase
        .from("sessions")
        .update({ transcript: serialised })
        .eq("id", sessionId)
        .eq("user_id", authState.user.id);

      if (error) throw error;

      return { success: true };
    } catch (error) {
      console.error("Error updating session transcript:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Fetch unlinked sessions (meetings not linked to a project) from all notetaker sources.
   * @param {string} accountId - The account ID to filter by.
   * @returns {Promise<{success: boolean, data?: object[], error?: string}>} Result.
   */
  const getUnlinkedSessions = async (accountId) => {
    try {
      const { data, error } = await supabase
        .from("sessions")
        .select("id, name, summary, source, extraction_type, created_at, source_metadata")
        .eq("account_id", accountId)
        .in("source", ["fathom", "recall"])
        .is("project_id", null)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return { success: true, data: data || [] };
    } catch (error) {
      console.error("Error fetching unlinked sessions:", error);
      return { success: false, error: error.message, data: [] };
    }
  };

  /**
   * Fetch the Recall.ai integration record for the current user + account.
   * @returns {Promise<{success: boolean, data?: object, error?: string}>} Result.
   */
  const getRecallIntegration = async () => {
    try {
      if (!authState.user || !authState.activeAccount) {
        return { success: false, error: "User not authenticated." };
      }

      const { data, error } = await supabase
        .from("integrations")
        .select("recall_api_key, recall_region, recall_webhook_id")
        .eq("user_id", authState.user.id)
        .eq("account_id", authState.activeAccount.id)
        .maybeSingle();

      if (error) throw error;

      return { success: true, data: data || null };
    } catch (error) {
      console.error("Error fetching Recall integration:", error);
      return { success: false, error: error.message, data: null };
    }
  };

  // saveRecallIntegration removed — RECALL_API_KEY and RECALL_REGION are now
  // configured as Supabase Edge Function secrets, not stored per-user in the DB.

  const addRequirement = async (requirementData) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const acceptanceCriteriaRaw = requirementData.acceptanceCriteria;
      const acceptanceCriteriaText =
        Array.isArray(acceptanceCriteriaRaw) && acceptanceCriteriaRaw.length > 0
          ? JSON.stringify(acceptanceCriteriaRaw)
          : null;

      const { data, error } = await supabase
        .from("requirements")
        .insert([
          {
            project_id: requirementData.projectId,
            session_id: requirementData.sessionId,
            user_id: authState.user.id,
            title: requirementData.title,
            description: requirementData.description,
            type: requirementData.type,
            priority: requirementData.priority || "Medium",
            acceptance_criteria: acceptanceCriteriaText,
            created_at: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Update the project with the new requirement using dedicated action
      dispatch({
        type: ActionTypes.ADD_REQUIREMENT,
        payload: {
          projectId: requirementData.projectId,
          requirement: mapRequirementRow(data),
        }
      });

      return { success: true, data };
    } catch (error) {
      console.error("Error adding requirement:", error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Add several requirements to one project in a single insert (one round trip, one state
   * update). Items use the same fields as addRequirement; `sessionId` may be set per item or
   * once in options.
   * @param {string} projectId - Project id.
   * @param {Array<{title: string, description?: string, type?: string, priority?: string, acceptanceCriteria?: string[], sessionId?: string|null}>} items - Requirements to add.
   * @param {{sessionId?: string|null}} [options] - Session every item comes from (unless the item sets its own).
   * @returns {Promise<{success: boolean, data?: object[], count?: number, error?: string}>} Inserted rows.
   */
  const addRequirements = async (projectId, items, { sessionId = null } = {}) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");
      if (!projectId) throw new Error("A project is required");
      const list = (Array.isArray(items) ? items : []).filter((item) => item && String(item.title || "").trim());
      if (!list.length) return { success: true, data: [], count: 0 };

      const createdAt = new Date().toISOString();
      const rows = list.map((item) => ({
        project_id: projectId,
        session_id: item.sessionId !== undefined ? item.sessionId : sessionId,
        user_id: authState.user.id,
        title: item.title,
        description: item.description,
        type: item.type,
        priority: item.priority || "Medium",
        acceptance_criteria:
          Array.isArray(item.acceptanceCriteria) && item.acceptanceCriteria.length > 0
            ? JSON.stringify(item.acceptanceCriteria)
            : null,
        created_at: createdAt,
      }));

      const { data, error } = await supabase.from("requirements").insert(rows).select();
      if (error) throw error;

      const inserted = data || [];
      dispatch({
        type: ActionTypes.ADD_REQUIREMENTS,
        payload: { projectId, requirements: inserted.map(mapRequirementRow) },
      });
      return { success: true, data: inserted, count: inserted.length };
    } catch (error) {
      console.error("Error adding requirements:", error);
      return { success: false, error: error.message };
    }
  };

  const updateRequirement = async (projectId, requirementId, updates) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const acceptanceCriteriaText =
        Array.isArray(updates.acceptanceCriteria) && updates.acceptanceCriteria.length > 0
          ? JSON.stringify(updates.acceptanceCriteria)
          : null;

      const updatePayload = {
        title: updates.title,
        description: updates.description,
        type: updates.type,
        priority: updates.priority,
      };
      if (updates.acceptanceCriteria !== undefined) {
        updatePayload.acceptance_criteria = acceptanceCriteriaText;
      }

      const { data, error } = await supabase
        .from("requirements")
        .update(updatePayload)
        .eq("id", requirementId)
        .eq("user_id", authState.user.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Functional update: back-to-back changes (bulk edits) each apply to the latest state.
      dispatch({
        type: ActionTypes.UPDATE_REQUIREMENT,
        payload: {
          projectId,
          requirementId,
          patch: {
            title: data.title,
            description: data.description,
            type: data.type,
            priority: data.priority,
            acceptanceCriteria: parseAcceptanceCriteria(data.acceptance_criteria),
          },
        },
      });
      return { success: true, data };
    } catch (error) {
      console.error("Error updating requirement:", error);
      return { success: false, error: error.message };
    }
  };

  const deleteRequirement = async (projectId, requirementId) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("requirements")
        .delete()
        .eq("id", requirementId)
        .eq("user_id", authState.user.id);

      if (error) {
        throw error;
      }

      // Functional update: back-to-back deletes (bulk, duplicates) each apply to the latest state.
      dispatch({ type: ActionTypes.DELETE_REQUIREMENT, payload: { projectId, requirementId } });
      return { success: true };
    } catch (error) {
      console.error("Error deleting requirement:", error);
      return { success: false, error: error.message };
    }
  };

  const addGeneratedDocument = async (documentData) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const row = {
        project_id: documentData.projectId,
        user_id: authState.user.id,
        name: documentData.name,
        content: documentData.content,
        template_id: documentData.templateId,
        template_name: documentData.templateName,
        requirements_count: documentData.requirementsCount || 0,
        created_at: new Date().toISOString(),
      };
      // A document made from a template starts with a copy of the template's page setup
      // (`documentData.pageSetup` when the caller has it, else the loaded template's). Later
      // template edits do not change it. No key at all when there is nothing to copy.
      const pageSetup = pageSetupForNewDocument(
        documentData.pageSetup !== undefined
          ? documentData.pageSetup
          : documentData.templateId
            ? stateRef.current.templates.find((t) => t.id === documentData.templateId)?.pageSetup
            : null
      );
      if (pageSetup) row.page_setup = pageSetup;

      const { data, error } = await writeWithOptionalPageSetup(row, (values) =>
        supabase.from("generated_documents").insert([values]).select().single()
      );

      if (error) {
        throw error;
      }

      dispatch({
        type: ActionTypes.UPSERT_GENERATED_DOCUMENT,
        payload: { projectId: documentData.projectId, document: mapGeneratedDocumentRow(data) },
      });
      return { success: true, data };
    } catch (error) {
      console.error("Error adding generated document:", error);
      return { success: false, error: error.message };
    }
  };

  const updateGeneratedDocument = async (projectId, documentId, newContent) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const { data, error } = await supabase
        .from("generated_documents")
        .update({
          content: newContent,
        })
        .eq("id", documentId)
        .eq("user_id", authState.user.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const patch = { content: data.content };
      if (data.updated_at !== undefined) patch.updatedAt = data.updated_at;
      dispatch({ type: ActionTypes.PATCH_GENERATED_DOCUMENT, payload: { documentId, patch } });
      return { success: true, data };
    } catch (error) {
      console.error("Error updating generated document:", error);
      return { success: false, error: error.message };
    }
  };

  const deleteGeneratedDocument = async (projectId, documentId) => {
    try {
      if (!authState.user) throw new Error("User not authenticated");

      const { error } = await supabase
        .from("generated_documents")
        .delete()
        .eq("id", documentId)
        .eq("user_id", authState.user.id);

      if (error) {
        throw error;
      }

      dispatch({ type: ActionTypes.DELETE_GENERATED_DOCUMENT, payload: { projectId, documentId } });
      return { success: true };
    } catch (error) {
      console.error("Error deleting generated document:", error);
      return { success: false, error: error.message };
    }
  };

  // Customer actions
  const createCustomer = async (customerData) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const { data, error } = await supabase
        .from("customers")
        .insert([
          {
            ...customerData,
            user_id: authState.user.id,
            account_id: authState.activeAccount.id, // Add the active account ID
          },
        ])
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Map database fields to frontend conventions
      const mappedCustomer = {
        id: data.id,
        name: data.name,
        description: data.description,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        company_website: data.company_website,
        address: data.address,
        domain: data.domain || [],
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        userId: data.user_id,
      };

      dispatch({ type: ActionTypes.ADD_CUSTOMER, payload: mappedCustomer });
      return { success: true, data: mappedCustomer };
    } catch (error) {
      console.error("Error creating customer:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  const updateCustomer = async (customerId, updates) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const { data, error } = await supabase
        .from("customers")
        .update(updates)
        .eq("id", customerId)
        .eq("account_id", authState.activeAccount.id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      // Map database fields to frontend conventions
      const mappedCustomer = {
        id: data.id,
        name: data.name,
        description: data.description,
        contact_email: data.contact_email,
        contact_phone: data.contact_phone,
        company_website: data.company_website,
        address: data.address,
        domain: data.domain || [],
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        userId: data.user_id,
      };

      dispatch({ type: ActionTypes.UPDATE_CUSTOMER, payload: mappedCustomer });
      return { success: true, data: mappedCustomer };
    } catch (error) {
      console.error("Error updating customer:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  const deleteCustomer = async (customerId) => {
    try {
      if (!authState.activeAccount) throw new Error("User not authenticated");

      dispatch({ type: ActionTypes.SET_LOADING, payload: true });

      const { error } = await supabase
        .from("customers")
        .delete()
        .eq("id", customerId)
        .eq("account_id", authState.activeAccount.id);

      if (error) {
        throw error;
      }

      dispatch({ type: ActionTypes.DELETE_CUSTOMER, payload: customerId });
      return { success: true };
    } catch (error) {
      console.error("Error deleting customer:", error);
      dispatch({ type: ActionTypes.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  /**
   * Find a customer whose domain array contains the given domain string.
   * @param {string} domainToFind - Domain to search for (e.g. "example.com").
   * @returns {object|null} The matching customer object, or null.
   */
  const findCustomerByDomain = (domainToFind) => {
    if (!domainToFind) return null;
    const normalized = domainToFind.toLowerCase().trim();
    return (
      state.customers.find(
        (c) =>
          Array.isArray(c.domain) &&
          c.domain.some((d) => d.toLowerCase().trim() === normalized)
      ) || null
    );
  };

  /**
   * Find all customers whose domain arrays contain the given domain string.
   * @param {string} domainToFind - Domain to search for (e.g. "example.com").
   * @returns {Array<object>} Array of matching customer objects.
   */
  const findCustomersByDomain = (domainToFind) => {
    if (!domainToFind) return [];
    const normalized = domainToFind.toLowerCase().trim();
    return state.customers.filter(
      (c) =>
        Array.isArray(c.domain) &&
        c.domain.some((d) => d.toLowerCase().trim() === normalized)
    );
  };

  /**
   * Update one generated document in local state only (no database query). Used after an
   * editor save so lists reflect the new content without reloading every project.
   * @param {string} documentId - Document id.
   * @param {{content?: string, updatedAt?: string, name?: string, pageSetup?: object|null}} patch - Fields to merge.
   * @returns {void}
   */
  const patchGeneratedDocumentLocal = (documentId, patch = {}) => {
    if (!documentId || !patch || typeof patch !== "object") return;
    const allowed = {};
    if (typeof patch.content === "string") allowed.content = patch.content;
    if (patch.updatedAt !== undefined) allowed.updatedAt = patch.updatedAt;
    if (typeof patch.name === "string" && patch.name) allowed.name = patch.name;
    if (patch.pageSetup === null || (patch.pageSetup && typeof patch.pageSetup === "object")) allowed.pageSetup = patch.pageSetup;
    if (!Object.keys(allowed).length) return;
    dispatch({ type: ActionTypes.PATCH_GENERATED_DOCUMENT, payload: { documentId, patch: allowed } });
  };

  /**
   * Add or update one generated document in local state only (no database query), e.g. when a
   * generation job finishes and the worker has inserted the row server-side. Accepts a
   * generated_documents row (snake_case; a partial select is fine, missing fields keep their
   * current value) or a local document with `projectId`.
   * @param {object} doc - Document row; needs `id` and `project_id` (or `projectId`).
   * @returns {boolean} False when it could not be applied locally (missing ids, or the project is
   *   not loaded): the caller should fall back to loadProjects().
   */
  const upsertGeneratedDocumentLocal = (doc) => {
    if (!doc || typeof doc !== "object" || !doc.id) return false;
    const projectId = doc.project_id ?? doc.projectId;
    if (!projectId) return false;
    if (!stateRef.current.projects.some((p) => p.id === projectId)) return false;
    dispatch({
      type: ActionTypes.UPSERT_GENERATED_DOCUMENT,
      payload: { projectId, document: mapGeneratedDocumentRow(doc) },
    });
    return true;
  };

  const actions = {
    // Data loading
    loadCustomers,
    loadProjects,
    loadTemplates,
    loadTemplate,
    loadTemplateContents,

    // Customer actions
    createCustomer,
    updateCustomer,
    deleteCustomer,

    // Project actions
    createProject,
    updateProject,
    deleteProject,

    // Template actions
    createTemplate,
    updateTemplate,
    deleteTemplate,

    // Session actions
    addSession,
    updateSession,
    deleteSession,
    linkSession,
    updateSessionTranscript,
    getUnlinkedSessions,
    getRecallIntegration,

    // Requirement actions
    addRequirement,
    addRequirements,
    updateRequirement,
    deleteRequirement,

    // Generated document actions
    addGeneratedDocument,
    updateGeneratedDocument,
    deleteGeneratedDocument,
    patchGeneratedDocumentLocal,
    upsertGeneratedDocumentLocal,

    // Current item setters
    setCurrentProject,
    setCurrentTemplate,

    // Utility actions
    clearError,
    getProjectById,
    getTemplateById,

    // Domain matching
    findCustomerByDomain,
    findCustomersByDomain,
  };

  // Stable action identities: each method delegates to this render's implementation, so it
  // always sees the latest state while consumers (effects, memoized callbacks, React.memo
  // children) no longer change on every provider render.
  const latestActionsRef = useRef(actions);
  latestActionsRef.current = actions;
  const stableActions = useMemo(() => {
    const wrapped = {};
    Object.keys(latestActionsRef.current).forEach((name) => {
      wrapped[name] = (...args) => latestActionsRef.current[name](...args);
    });
    return wrapped;
  }, []);

  const value = useMemo(() => ({ state, actions: stableActions }), [state, stableActions]);

  return (
    <AppActionsContext.Provider value={stableActions}>
      <AppContext.Provider value={value}>
        {children}
      </AppContext.Provider>
    </AppActionsContext.Provider>
  );
}

// Custom hook to use the context
export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useApp must be used within an AppProvider");
  }
  return context;
}

/**
 * AppContext actions without subscribing to state (the object never changes), for components
 * that only write - e.g. the document editor - so data reloads do not re-render them.
 * @returns {object} Actions (same methods as useApp().actions).
 */
export function useAppActions() {
  const actions = useContext(AppActionsContext);
  if (!actions) {
    throw new Error("useAppActions must be used within an AppProvider");
  }
  return actions;
}

export { parseTranscript, getTranscriptContent, appReducer, ActionTypes as AppActionTypes, mapGeneratedDocumentRow };
export default AppContext;
